import { motion } from "motion/react";
import { Baby, BookOpen, GraduationCap, ArrowRight, X } from "lucide-react";
import { useState } from "react";



const admissionLevels = [
  {
    icon: Baby,
    title: "Early Years",
    ages: "Ages 3-6",
    description:
      "A nurturing foundation where young minds begin their educational journey through play-based learning.",
    features: [
      "Play-based curriculum",
      "Social development",
      "Early literacy & numeracy",
    ],
    color: "from-red-500 to-red-600",
    bgColor: "bg-red-50",
  },
  {
    icon: BookOpen,
    title: "Junior School",
    ages: "Ages 7-10",
    description:
      "Building strong academic foundations while developing critical thinking and creativity.",
    features: ["Core subjects mastery", "Creative arts", "Physical education"],
    color: "from-blue-600 to-blue-700",
    bgColor: "bg-blue-50",
  },
  {
    icon: GraduationCap,
    title: "Upper School",
    ages: "Ages 11-14",
    description:
      "Preparing students for secondary education with advanced academics and leadership skills.",
    features: ["Advanced curriculum", "Leadership programs", "Career guidance"],
    color: "from-blue-800 to-blue-900",
    bgColor: "bg-slate-50",
  },
];

export function Admissions() {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const mailtoLink = `mailto:info@hilltop.co.ke?subject=School Tour Request&body=
Name: ${form.name}%0D%0A
Phone: ${form.phone}%0D%0A
Email: ${form.email}%0D%0A
%0D%0AUser requested a campus tour.`;

    window.location.href = mailtoLink;
  };

  return (
    <section
      id="admissions"
      className="py-24 bg-gradient-to-b from-white to-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
            Join Our Community
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Admission to Hilltop
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Choose the right program for your child's age and watch them thrive
            in our supportive environment.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {admissionLevels.map((level, index) => (
            <motion.div
              key={level.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="group"
            >
              <div
                className={`${level.bgColor} rounded-3xl p-8 h-full border border-slate-100 hover:border-transparent hover:shadow-2xl transition-all duration-500 hover:-translate-y-2`}
              >
                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${level.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                >
                  <level.icon className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  {level.title}
                </h3>
                <span className="inline-block px-3 py-1 bg-white rounded-full text-sm font-medium text-slate-600 mb-4 shadow-sm">
                  {level.ages}
                </span>

                <p className="text-slate-600 mb-6 leading-relaxed">
                  {level.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {level.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center text-slate-700"
                    >
                      <div
                        className={`w-2 h-2 rounded-full bg-gradient-to-r ${level.color} mr-3`}
                      />
                      {feature}
                    </li>
                  ))}
                </ul>

                <a
                  href="mailto:info@hilltop.co.ke?subject=Application%20Request&body=Hello%20Hilltop%20Team,%0D%0A%0D%0AI%20would%20like%20to%20apply."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-slate-900 font-semibold group-hover:text-red-600 transition-colors"
                >
                  <span>Apply Now</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 bg-gradient-to-r from-blue-800 to-blue-900 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/20 rounded-full blur-3xl" />

          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Begin?
            </h3>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
              Schedule a school tour or apply now and discover why Hilltop Preparatory is the
              perfect choice for your child's education.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowModal(true)}
                className="bg-white text-blue-900 px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                Schedule a Tour
              </button>
              <a
                href="mailto:info@hilltop.co.ke?subject=Application%20Request&body=Hello%20Hilltop%20Team,%0D%0A%0D%0AI%20would%20like%20to%20apply."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-red-700 hover:shadow-xl transition-all duration-300"
              >
                Apply Now
              </a>
            </div>
          </div>
        </motion.div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-slate-500 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Schedule a School Tour
            </h3>
            <p className="text-slate-600 mb-6">
              Please enter your details and we will get back to you as soon as possible.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Full Name"
                required
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                  setForm({ ...form, name: e.target.value })
                }
              />
              <input
                type="tel"
                placeholder="Phone Number"
                required
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                  setForm({ ...form, phone: e.target.value })
                }
              />
              <input
                type="email"
                placeholder="Email Address"
                required
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) =>
                  setForm({ ...form, email: e.target.value })
                }
              />

              <button
                type="submit"
                className="w-full bg-blue-900 text-white py-3 rounded-xl font-semibold hover:bg-blue-800 transition"
              >
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
